import { Box, Button, Grid, Input, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import OrganizationSettingsClient, { OrganizationSettingsComplete, OrganizationSettingsErrors } from "../apiClients/OrganizationSettingsClient";
import BusySpinner from "../components/BusySpinner";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { useErrorDisplay } from "../components/ErrorDisplay";
import OnlyForRole from "../components/OnlyForRole";
import TableHeaderCell from "../components/TableHeaderCell";
import { useAccessInfo } from "../contexts/AccessTokenProvider";
import { Securables, UpsertAction } from "../Enums";
import { CastToBoolean, IsBetween } from "../Helpers";
import useViewportMode from "../hooks/useViewportMode";
import { MobileFormStack } from "../components/responsive";

export interface OrganizationSettingsComponentProps {
    mode: UpsertAction;
}

const OrganizationSettingsComponent = ({ mode }: OrganizationSettingsComponentProps) => {

    const systemDevPlanMin = 0;
    const systemDevPlanMax = 21;

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>();

    const access = useAccessInfo();
    const { mode: viewportMode, isDesktop, isTablet, isLandscape } = useViewportMode();

    const [settings, setSettings] = useState<OrganizationSettingsComplete>(null);
    const [dataHasChanged, setDataHasChanged] = useState<boolean>(false);

    const loadData = async () => {
        try {
            setBusy(true);

            let settings = await new OrganizationSettingsClient().GetOrganizationSettings();
            setSettings(settings);
        }
        catch (error) {
            setError(error, OrganizationSettingsErrors.GetAll);
        }
        finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    const handleSettingChange = (e, name: string, isBool: boolean = false) => {
        if (isBool) {
            setSettings({ ...settings, [name]: CastToBoolean(e.target.value) })
        }
        else {
            setSettings({ ...settings, [name]: e.target.value })
        }
        setDataHasChanged(true);
    }

    const handleDevelopmentPlanMinMaxChange = (e, name: string) => {
        const value = parseInt(e.target.value.toString());

        if ((name === 'DevelopmentPlanMin' && IsBetween(value, systemDevPlanMin, settings.DevelopmentPlanMax))
            || (name === 'DevelopmentPlanMax' && IsBetween(value, settings.DevelopmentPlanMin, systemDevPlanMax))) {
            setSettings({ ...settings, [name]: value })
            setDataHasChanged(true);
        }
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);

            const orgClient = new OrganizationSettingsClient();
            const onlySettings = orgClient.ExtractOnlySettings(settings);
            await new OrganizationSettingsClient().UpdateSettings(onlySettings);

            await access.reloadOrgSettings();

            setDataHasChanged(false);
        }
        catch (error) {
            setError(error, OrganizationSettingsErrors.Update);
        }
        finally {
            setBusy(false);
        }
    }

    const booleanControl = (id: string, name: string, value?: boolean) => mode === UpsertAction.Update
        ? <Select
            id={id}
            value={value?.toString() ?? ''}
            onChange={(e) => handleSettingChange(e, name, true)}
            size="small"
            fullWidth>
            <MenuItem value='false'>False</MenuItem>
            <MenuItem value='true'>True</MenuItem>
        </Select>
        : <Typography>{value?.toString()}</Typography>;

    const compactSetting = (name: string, description: string, control: JSX.Element) => (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
            <Stack spacing={1.25}>
                <Box>
                    <Typography fontWeight={700}>{name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {description}
                    </Typography>
                </Box>
                {control}
            </Stack>
        </Paper>
    );

    return (
        <>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                {isDesktop ? <Grid container direction="row" data-viewport-mode={viewportMode}>
                    <Grid item>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell>Name</TableHeaderCell>
                                        <TableHeaderCell>Description</TableHeaderCell>
                                        <TableHeaderCell>Value</TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Default Profile Name</TableCell>
                                        <TableCell>{settings?.Descriptions['DefaultProfileName']}</TableCell>
                                        <TableCell>
                                            <TextField fullWidth
                                                aria-readonly={mode === UpsertAction.View}
                                                value={settings?.DefaultProfileName ?? ''}
                                                onChange={(e) => handleSettingChange(e, 'DefaultProfileName')} />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Display First Name Then Last</TableCell>
                                        <TableCell>
                                            {settings?.Descriptions['DisplayFirstNameThenLast']}</TableCell>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.Update}>
                                            <TableCell>
                                                <Select
                                                    id="select-firstNameThenLast"
                                                    value={settings?.DisplayFirstNameThenLast.toString() ?? ''}
                                                    onChange={(e) => handleSettingChange(e, 'DisplayFirstNameThenLast', true)}
                                                    size="small"
                                                    fullWidth={true}>
                                                    <MenuItem value='false'>False</MenuItem>
                                                    <MenuItem value='true'>True</MenuItem>
                                                </Select>
                                            </TableCell>
                                        </ConditionalDisplay>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                                            <TableCell>{settings?.DisplayFirstNameThenLast.toString()}</TableCell>
                                        </ConditionalDisplay>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Remove Assesment Selections</TableCell>
                                        <TableCell>{settings?.Descriptions['RemoveAssesmentSelections']}</TableCell>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.Update}>
                                            <TableCell>
                                                <Select
                                                    id="select-RemoveAssesmentSelections"
                                                    value={settings?.RemoveAssesmentSelections.toString() ?? ''}
                                                    onChange={(e) => handleSettingChange(e, 'RemoveAssesmentSelections', true)}
                                                    size="small"
                                                    fullWidth={true}>
                                                    <MenuItem value='false'>False</MenuItem>
                                                    <MenuItem value='true'>True</MenuItem>
                                                </Select>
                                            </TableCell>
                                        </ConditionalDisplay>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                                            <TableCell>{settings?.RemoveAssesmentSelections.toString()}</TableCell>
                                        </ConditionalDisplay>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Development Plan Min</TableCell>
                                        <TableCell>{settings?.Descriptions['DevelopmentPlanMin']}</TableCell>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.Update}>
                                            <TableCell>
                                                <Input
                                                    id="input-DevelopmentPlanMin"
                                                    name="DevelopmentPlanMin" fullWidth
                                                    value={settings?.DevelopmentPlanMin ?? 3}
                                                    type="number"
                                                    inputProps={{ min: systemDevPlanMin }}
                                                    onChange={(e) => handleDevelopmentPlanMinMaxChange(e, 'DevelopmentPlanMin')}
                                                />
                                            </TableCell>
                                        </ConditionalDisplay>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                                            <TableCell>{settings?.DevelopmentPlanMin.toString()}</TableCell>
                                        </ConditionalDisplay>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Development Plan Max</TableCell>
                                        <TableCell>{settings?.Descriptions['DevelopmentPlanMax']}</TableCell>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.Update}>
                                            <TableCell>
                                                <Input
                                                    id="input-DevelopmentPlanMax"
                                                    name="DevelopmentPlanMax" fullWidth
                                                    type="number"
                                                    value={(settings?.DevelopmentPlanMax as number) ?? 5}
                                                    inputProps={{ max: systemDevPlanMax }}
                                                    onChange={(e) => handleDevelopmentPlanMinMaxChange(e, 'DevelopmentPlanMax')}
                                                />
                                            </TableCell>
                                        </ConditionalDisplay>
                                        <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                                            <TableCell>{settings?.DevelopmentPlanMin.toString()}</TableCell>
                                        </ConditionalDisplay>
                                    </TableRow>
                                    <OnlyForRole role={Securables.synergist_admin} preventOverrides={true}>
                                        <TableRow>
                                            <TableCell>Subscription Override</TableCell>
                                            <TableCell>
                                                {settings?.Descriptions['SubscriptionOverride']}</TableCell>
                                            <ConditionalDisplay ifTrue={mode === UpsertAction.Update}>
                                                <TableCell>
                                                    <Select
                                                        id="select-SubscriptionOverride"
                                                        value={settings?.SubscriptionOverride.toString() ?? ''}
                                                        onChange={(e) => handleSettingChange(e, 'SubscriptionOverride', true)}
                                                        size="small"
                                                        fullWidth={true}>
                                                        <MenuItem value='false'>False</MenuItem>
                                                        <MenuItem value='true'>True</MenuItem>
                                                    </Select>
                                                </TableCell>
                                            </ConditionalDisplay>
                                            <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                                                <TableCell>{settings?.SubscriptionOverride.toString()}</TableCell>
                                            </ConditionalDisplay>
                                        </TableRow>
                                    </OnlyForRole>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                    <Grid item sx={{ paddingRight: '1em', paddingTop: '.5em', display: 'flex', flexDirection: 'row-reverse', width: '100%' }}>
                        <Button variant='outlined' disabled={!dataHasChanged} onClick={handleSubmit}>Update</Button>
                    </Grid>
                </Grid> : <Stack spacing={2} data-viewport-mode={viewportMode}>
                    <MobileFormStack tabletColumns={isTablet && isLandscape ? 2 : 1} viewportOverride={viewportMode}>
                        {compactSetting(
                            'Default Profile Name',
                            settings?.Descriptions['DefaultProfileName'],
                            <TextField
                                fullWidth
                                aria-readonly={mode === UpsertAction.View}
                                value={settings?.DefaultProfileName ?? ''}
                                onChange={(e) => handleSettingChange(e, 'DefaultProfileName')}
                            />,
                        )}
                        {compactSetting(
                            'Display First Name Then Last',
                            settings?.Descriptions['DisplayFirstNameThenLast'],
                            booleanControl('select-firstNameThenLast', 'DisplayFirstNameThenLast', settings?.DisplayFirstNameThenLast),
                        )}
                        {compactSetting(
                            'Remove Assesment Selections',
                            settings?.Descriptions['RemoveAssesmentSelections'],
                            booleanControl('select-RemoveAssesmentSelections', 'RemoveAssesmentSelections', settings?.RemoveAssesmentSelections),
                        )}
                        {compactSetting(
                            'Development Plan Min',
                            settings?.Descriptions['DevelopmentPlanMin'],
                            mode === UpsertAction.Update
                                ? <Input
                                    id="input-DevelopmentPlanMin"
                                    name="DevelopmentPlanMin"
                                    fullWidth
                                    value={settings?.DevelopmentPlanMin ?? 3}
                                    type="number"
                                    inputProps={{ min: systemDevPlanMin }}
                                    onChange={(e) => handleDevelopmentPlanMinMaxChange(e, 'DevelopmentPlanMin')}
                                />
                                : <Typography>{settings?.DevelopmentPlanMin.toString()}</Typography>,
                        )}
                        {compactSetting(
                            'Development Plan Max',
                            settings?.Descriptions['DevelopmentPlanMax'],
                            mode === UpsertAction.Update
                                ? <Input
                                    id="input-DevelopmentPlanMax"
                                    name="DevelopmentPlanMax"
                                    fullWidth
                                    type="number"
                                    value={(settings?.DevelopmentPlanMax as number) ?? 5}
                                    inputProps={{ max: systemDevPlanMax }}
                                    onChange={(e) => handleDevelopmentPlanMinMaxChange(e, 'DevelopmentPlanMax')}
                                />
                                : <Typography>{settings?.DevelopmentPlanMax.toString()}</Typography>,
                        )}
                        <OnlyForRole role={Securables.synergist_admin} preventOverrides={true}>
                            {compactSetting(
                                'Subscription Override',
                                settings?.Descriptions['SubscriptionOverride'],
                                booleanControl('select-SubscriptionOverride', 'SubscriptionOverride', settings?.SubscriptionOverride),
                            )}
                        </OnlyForRole>
                    </MobileFormStack>
                    <Box sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        bgcolor: 'background.paper',
                        borderTop: 1,
                        borderColor: 'divider',
                        px: 1,
                        pt: 1.25,
                        pb: 'calc(10px + env(safe-area-inset-bottom, 0px))',
                    }}>
                        <Button
                            variant='outlined'
                            disabled={!dataHasChanged}
                            onClick={handleSubmit}
                            sx={{ minHeight: 44, width: viewportMode === 'phone' ? '100%' : 'auto' }}>
                            Update
                        </Button>
                    </Box>
                </Stack>}
            </BusySpinner>

        </>
    );
}

export default OrganizationSettingsComponent;
